import { type ReactNode, useMemo, useState } from 'react';
import { useRouteLoaderData } from 'react-router';

import type { OrderStatusResponse } from '~/interfaces/order_status';
import getProductVariantCoachingSessionDetails from '~/utils/getProductVariantCoachingSessionDetails';

import type { ProductLoaderData } from '../$slug';
import { ClientSecretContext } from './ClientSecretContext';
import type { RedemptionState } from './InstantRedemptionModal';

export function ClientSecretProvider({ children }: { children: ReactNode }) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [redemptionToken, setRedemptionToken] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [redemptionState, setRedemptionState] = useState<RedemptionState>('loading');
  const [orderStatusData, setOrderStatusData] = useState<OrderStatusResponse['order'] | null>(null);
  const [redemptionErrorMessage, setRedemptionErrorMessage] = useState<string | null>(null);
  const [showFreeProductCheckoutModal, setShowFreeProductCheckoutModal] = useState(false);

  const { product } = useRouteLoaderData('routes/products/$slug') as ProductLoaderData;
  const shouldInstantlyRedeem = useMemo(() => {
    if (!product) {
      return false;
    }

    const { isCoachingSession, canInstantlyRedeem: canInstantlyRedeemCoachingSession } =
      getProductVariantCoachingSessionDetails(product);

    if (isCoachingSession) {
      return canInstantlyRedeemCoachingSession;
    }

    return true;
  }, [product]);

  return (
    <ClientSecretContext.Provider
      value={{
        clientSecret,
        setClientSecret,
        redemptionToken,
        setRedemptionToken,
        showThankYou,
        setShowThankYou,
        showRedemptionModal,
        setShowRedemptionModal,
        redemptionState,
        setRedemptionState,
        orderStatusData,
        setOrderStatusData,
        redemptionErrorMessage,
        setRedemptionErrorMessage,
        shouldInstantlyRedeem,
        showFreeProductCheckoutModal,
        setShowFreeProductCheckoutModal,
      }}
    >
      {children}
    </ClientSecretContext.Provider>
  );
}
